import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { debounceTime, filter, map, of, switchMap } from 'rxjs';

import { EffectsBase } from '@ps/core-data-access';
import type { Quota, SurveyMetadataForm } from '@ps/survey-utils';

import { UnicornsService } from '../../services/unicorns/unicorns.service';
import { SurveyCreationAudienceActions } from '../audience/survey-creation-audience.actions';
import { SurveyCreationCoreApiActions } from '../core/survey-creation-core.actions';
import { selectSurveyFormState } from '../audience/survey-creation-audience.selectors';
import { SurveyCreationInsightsApiActions } from './survey-creation-insights.actions';

interface ImprobableQuotaLookup {
  readonly metadata: SurveyMetadataForm;
  readonly nestedQuotas: Quota[];
}

@Injectable()
export class SurveyCreationImprobableQuotaEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _service = inject(UnicornsService);

  /**
   * Re-checked whenever the quotas or the locale could have changed. Debounced
   * with feasibility's own 500ms so a burst of quota edits sends one request.
   */
  public readonly findImprobableQuotas$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess,
        SurveyCreationAudienceActions.updateQuotas,
        SurveyCreationAudienceActions.updateMetadata,
      ),
      debounceTime(500),
      concatLatestFrom(() => this._store.select(selectSurveyFormState)),
      map(([, formState]) => ({
        metadata: formState.metadata.value,
        nestedQuotas: this._service.nestedQuotasIn(formState.quotas.value),
      })),
      filter((lookup): lookup is ImprobableQuotaLookup => !!lookup.metadata),
      switchMap(({ metadata, nestedQuotas }) =>
        this._service.findUnicorns(metadata, nestedQuotas).pipe(
          map((quotaIds) =>
            SurveyCreationInsightsApiActions.findImprobableQuotasSuccess({
              quotaIds,
            }),
          ),
          this.psCatchError((error) =>
            of(
              SurveyCreationInsightsApiActions.findImprobableQuotasFailure({
                error: error.message || 'Could not check for improbable quotas',
              }),
            ),
          ),
        ),
      ),
    ),
  );
}
